import React, { useEffect } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "./lib/auth";
import Layout from "./components/Layout";
import HomePage from "./pages/HomePage";
import AtelierPage from "./pages/AtelierPage";
import ProPage from "./pages/ProPage";
import RealisationsPage from "./pages/RealisationsPage";
import RealisationDetailPage from "./pages/RealisationDetailPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import ContactPage from "./pages/ContactPage";
import LegalPage from "./pages/LegalPage";
import ZonesPage from "./pages/ZonesPage";
import FaqPage from "./pages/FaqPage";
import CookiesPolicyPage from "./pages/CookiesPolicyPage";
import CookieBanner from "./components/CookieBanner";
import { installNetworkBlocker } from "./lib/consent";

// Install PostHog / analytics blocker BEFORE React mounts anything else
installNetworkBlocker();

// Generate a session id (used for anonymous unique-visitor counting)
function getSessionId() {
  try {
    let sid = sessionStorage.getItem("pv_sid");
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("pv_sid", sid);
    }
    return sid;
  } catch { return ""; }
}

import AdminLayout, { AdminLogin, AuthCallback } from "./admin/AdminLayout";
import {
  AdminDashboard, AdminContent, AdminUniverses, AdminClientLogos, AdminBestSellers,
  AdminGallery, AdminGalleryCategories, AdminProRealizations, AdminBlog, AdminReviews, AdminFAQs, AdminMarkers, AdminLegal, AdminMessages, AdminFranchise, AdminExportImport,
} from "./admin/AdminPages";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const Analytics = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    fetch(`/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        path: pathname,
        referrer: document.referrer || "",
        session_id: getSessionId(),
      }),
    }).catch(() => {});
  }, [pathname]);
  return null;
};

function AppRouter() {
  const location = useLocation();
  // Detect auth callback in URL fragment (runs BEFORE normal routing)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <Analytics />
      <Routes>
        <Route path="/" element={<Layout><HomePage /></Layout>} />
        <Route path="/atelier" element={<Layout><AtelierPage /></Layout>} />
        <Route path="/professionnels" element={<Layout><ProPage /></Layout>} />
        <Route path="/realisations" element={<Layout><RealisationsPage /></Layout>} />
        <Route path="/realisations/:slug" element={<Layout><RealisationDetailPage /></Layout>} />
        <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
        <Route path="/blog/:slug" element={<Layout><BlogPostPage /></Layout>} />
        <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
        <Route path="/mentions-legales" element={<Layout><LegalPage slug="mentions-legales" /></Layout>} />
        <Route path="/cgv" element={<Layout><LegalPage slug="cgv" /></Layout>} />
        <Route path="/politique-confidentialite" element={<Layout><LegalPage slug="politique-confidentialite" /></Layout>} />
        <Route path="/zones-desservies" element={<Layout><ZonesPage /></Layout>} />
        <Route path="/faq" element={<Layout><FaqPage /></Layout>} />
        <Route path="/politique-cookies" element={<Layout><CookiesPolicyPage /></Layout>} />

        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/callback" element={<AuthCallback />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="content" element={<AdminContent />} />
          <Route path="universes" element={<AdminUniverses />} />
          <Route path="client-logos" element={<AdminClientLogos />} />
          <Route path="best-sellers" element={<AdminBestSellers />} />
          <Route path="pro-realizations" element={<AdminProRealizations />} />
          <Route path="gallery" element={<AdminGallery />} />
          <Route path="gallery-categories" element={<AdminGalleryCategories />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="faqs" element={<AdminFAQs />} />
          <Route path="markers" element={<AdminMarkers />} />
          <Route path="legal" element={<AdminLegal />} />
          <Route path="messages" element={<AdminMessages />} />
          <Route path="franchise" element={<AdminFranchise />} />
          <Route path="export-import" element={<AdminExportImport />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <CookieBanner />
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              style: { background: "#171717", border: "1px solid rgba(154, 134, 64, 0.4)", color: "#DDD4B2" },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
